import { Alert, Badge, Button, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { getJeepRallyMediaUrl } from "../../../../../api/jeep-rally/api";
import { useGetRouteById } from "../../../../../api/jeep-rally/routes/routes.hooks";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import {
  getRouteApiErrorMessage,
  normalizeRoute,
} from "../utils/route.utils";

const DetailItem = ({ label, children }) => (
  <div className="mb-3">
    <div className="text-muted small mb-1">{label}</div>
    <div>{children ?? "-"}</div>
  </div>
);

const formatPoint = (point) => {
  if (!point) return "-";
  const title = point.title || point.name || "-";
  const lat = point.lat ?? point.latitude;
  const lng = point.lng ?? point.longitude;
  if (lat == null || lng == null) return title;
  return `${title} (${lat}, ${lng})`;
};

const RouteViewPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const canManage = isJeepRallyAdmin();

  const locationRoute = history.location.state?.route || null;

  const {
    data: routeData,
    isLoading,
    isError,
    error,
  } = useGetRouteById(id);

  const route = normalizeRoute(routeData) || locationRoute;
  const stages = Array.isArray(route?.stages)
    ? [...route.stages].sort(
        (a, b) => Number(a.stage_number || 0) - Number(b.stage_number || 0)
      )
    : [];
  const checkpoints = Array.isArray(route?.checkpoints) ? route.checkpoints : [];
  const trackMapUrl = getJeepRallyMediaUrl(
    route?.track_map_image || route?.trackMapImage
  );
  const isActive = route?.status !== "inactive";
  const routeId = route?._id || route?.id || id;

  const goBack = () => history.push("/jeep-rally/routes");
  const goEdit = () =>
    history.push(`/jeep-rally/routes/edit/${routeId}`, { route });

  if (isLoading && !locationRoute) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if ((isError && !locationRoute) || !route) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-3">
              {getRouteApiErrorMessage(
                error,
                "Route not found. Please open it from the Routes list."
              )}
            </Alert>
            <Button variant="outline-secondary" onClick={goBack}>
              Back to routes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to routes list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">View Route</h4>
          </div>
          {canManage ? (
            <Button variant="primary" size="sm" onClick={goEdit}>
              Edit
            </Button>
          ) : null}
        </div>

        <div className="card-body">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
            <div>
              <h5 className="mb-1">{route.title || route.name || "-"}</h5>
              <div className="text-muted small">
                Route ID: <code>{routeId}</code>
              </div>
            </div>
            <Badge bg={isActive ? "success" : "secondary"}>
              {route.status || "active"}
            </Badge>
          </div>

          <div className="row">
            <div className="col-md-3">
              <DetailItem label="Total distance">
                {route.total_distance_km === 0 || route.total_distance_km
                  ? `${route.total_distance_km} km`
                  : "-"}
              </DetailItem>
            </div>
            <div className="col-md-3">
              <DetailItem label="Estimated time">
                {route.estimated_time || "-"}
              </DetailItem>
            </div>
            <div className="col-md-3">
              <DetailItem label="Stages">{stages.length}</DetailItem>
            </div>
            <div className="col-md-3">
              <DetailItem label="Checkpoints">{checkpoints.length}</DetailItem>
            </div>
            <div className="col-md-12">
              <DetailItem label="Description">
                {route.description || "-"}
              </DetailItem>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="mb-3">Track map</h5>
            <div
              className="d-flex align-items-center justify-content-center overflow-hidden border rounded bg-light"
              style={{ minHeight: 200 }}
            >
              {trackMapUrl ? (
                <img
                  src={trackMapUrl}
                  alt="Track map"
                  style={{
                    width: "100%",
                    maxHeight: 360,
                    objectFit: "contain",
                  }}
                />
              ) : (
                <span className="text-muted small py-5">No track map uploaded</span>
              )}
            </div>
          </div>

          <div className="mb-4">
            <h5 className="mb-3">Stages ({stages.length})</h5>
            {!stages.length ? (
              <p className="text-muted mb-0">No stages on this route.</p>
            ) : (
              <div className="table-responsive border rounded">
                <table className="table table-striped mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 80 }}>#</th>
                      <th>Stage name</th>
                      <th>Start point</th>
                      <th>End point</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stages.map((stage, index) => {
                      const stageId = stage._id || stage.id || index;
                      const starting =
                        stage.startingPoint || stage.starting_point || null;
                      const ending =
                        stage.endingPoint || stage.ending_point || null;

                      return (
                        <tr key={stageId}>
                          <td>{stage.stage_number ?? index + 1}</td>
                          <td className="fw-semibold">
                            {stage.stage_name || "-"}
                          </td>
                          <td>{formatPoint(starting)}</td>
                          <td>{formatPoint(ending)}</td>
                          <td>{stage.stage_description || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {checkpoints.length > 0 ? (
            <div className="mb-0">
              <h5 className="mb-3">Checkpoints ({checkpoints.length})</h5>
              <div className="table-responsive border rounded">
                <table className="table table-striped mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Stage</th>
                      <th>Order</th>
                      <th>KM start</th>
                      <th>KM end</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkpoints.map((checkpoint, index) => {
                      const checkpointId =
                        checkpoint._id || checkpoint.id || index;
                      return (
                        <tr key={checkpointId}>
                          <td>{index + 1}</td>
                          <td>{checkpoint.title || "-"}</td>
                          <td>
                            {checkpoint.stage_number ??
                              checkpoint.stage_id ??
                              "-"}
                          </td>
                          <td>{checkpoint.order ?? "-"}</td>
                          <td>{checkpoint.km_start ?? "-"}</td>
                          <td>{checkpoint.km_end ?? "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={goBack}>
              Back
            </Button>
            {canManage ? (
              <Button variant="primary" onClick={goEdit}>
                Edit Route
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteViewPage;
